import React, { useEffect, useState } from 'react';
import { PlusCircle, MinusCircle, PencilLine, History } from 'lucide-react';
import api from '../lib/api';
import { formatIST } from '../lib/formatDate';

interface SheetChange {
  id: string;
  sheet_key: string;
  tab: string;
  change_type: 'added' | 'removed' | 'updated';
  row_label: string;
  column: string | null;
  old_value: string | null;
  new_value: string | null;
  context_person: string | null;
  detected_at: string;
}

const TYPE_STYLE: Record<string, { icon: any; badge: string }> = {
  added: { icon: PlusCircle, badge: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  removed: { icon: MinusCircle, badge: 'bg-rose-50 text-rose-700 border-rose-100' },
  updated: { icon: PencilLine, badge: 'bg-amber-50 text-amber-700 border-amber-100' },
};

/**
 * Shows a real, diff-derived change log (row/column/old-value -> new-value) computed by the
 * backend by comparing each Google Sheets sync snapshot against the previous one. Change
 * tracking starts from whenever this feature first ran — there is no historical audit trail
 * before that (Sheets' read-only values API has no revision/edit-author metadata).
 */
export default function RecentChangesPanel({ apiPath, label }: { apiPath: string; label: string }) {
  const [changes, setChanges] = useState<SheetChange[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(apiPath).then((res) => setChanges(res.data.changes || [])).catch(() => setChanges([])).finally(() => setLoading(false));
  }, [apiPath]);

  return (
    <div data-testid="recent-changes-panel" className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
        <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
          <History className="h-3 w-3" /> Recent Changes — {label}
        </span>
        <span className="text-[9px] font-mono bg-slate-50 text-slate-500 px-2.5 py-0.5 rounded-full border border-slate-200 uppercase font-bold">
          {changes.length} tracked
        </span>
      </div>
      {loading ? (
        <p className="text-xs text-slate-400 font-mono">Loading change log…</p>
      ) : changes.length === 0 ? (
        <p data-testid="recent-changes-empty" className="text-xs text-slate-400 leading-relaxed">
          No changes detected since sync tracking began. Once a value changes in the sheet on the next sync cycle, it will appear here — with the row, column, old vs. new value, and the row's owner where the sheet has one.
        </p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {changes.map((c) => {
            const style = TYPE_STYLE[c.change_type];
            const Icon = style.icon;
            return (
              <div key={c.id} data-testid="recent-change-row" className="flex items-start gap-3 p-3 rounded-2xl border border-slate-100">
                <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center border ${style.badge}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-100">{c.tab}</span>
                    <span className="text-xs font-semibold text-slate-700 truncate max-w-[220px]">{c.row_label}</span>
                    {c.context_person && <span className="text-[10px] text-slate-400">· {c.context_person}</span>}
                    <span className="text-[10px] font-mono text-slate-400 ml-auto whitespace-nowrap">{formatIST(c.detected_at)}</span>
                  </div>
                  <p className="text-xs mt-1 font-sans">
                    {c.change_type === 'added' && <span className="text-emerald-600">New row added</span>}
                    {c.change_type === 'removed' && <span className="text-rose-600">Row removed</span>}
                    {c.change_type === 'updated' && (
                      <>
                        <span className="text-slate-400 font-mono text-[10px]">{c.column}: </span>
                        <span className="text-rose-500 line-through">{c.old_value || '(empty)'}</span>
                        <span className="text-slate-400"> → </span>
                        <span className="text-emerald-600 font-semibold">{c.new_value || '(empty)'}</span>
                      </>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
